import { type AxiosError } from 'axios'
import produce from 'immer'
import React from 'react'
import {
  type QueryStatus,
  type QueryObserverOptions,
  type UseQueryResult
} from 'react-query'
import { type UseInfiniteQueryResult } from 'react-query/types/react/types'
import create from 'zustand'

import { getPageDataItemsFlat } from 'queries/queries-utils'
import { type PagedUserDefinedServiceResponse } from 'types/api/services/dependencies/dependency'
import { type UserDefinedService } from 'types/api/services/service'
import { createLookupMapFromArray } from 'utils/obj'

import { useGremlinPagination, useGremlinQuery } from './use-gremlin-query'

export interface ServicesState {
  status: QueryStatus
  services: {
    [id: string]: UserDefinedService
  }
  hasNextPage: boolean
  set: (fn: (state: ServicesState) => void) => void
  clearAll: () => void
}

const QUERY_KEY_BASE = 'SERVICES_PAGED'
export const serviceQueryKeys = {
  allPaged: [QUERY_KEY_BASE] as const
}

export const useServicesStore = create<ServicesState>((set) => ({
  status: 'loading',
  services: {},
  hasNextPage: true,
  set: (fn) => set(produce(fn)),
  clearAll: () =>
    set(
      produce((state) => {
        state.status = 'loading'
        state.services = {}
      })
    )
}))

export function useServices(
  enabled = true
): UseInfiniteQueryResult<PagedUserDefinedServiceResponse> {
  const set = useServicesStore((state) => state.set)
  const pagedAllFetchState =
    useGremlinPagination<PagedUserDefinedServiceResponse>(
      '/services/?pageSize=20',
      {},
      {
        onSuccess: (data) => {
          set((state) => {
            const all = getPageDataItemsFlat(data)
            state.services = createLookupMapFromArray(all, 'serviceId')
          })
        },
        onSettled: (data, error) => {
          set((state) => {
            state.status =
              error && (!data || typeof data === 'string') ? 'error' : 'success'
          })
        },
        queryKey: serviceQueryKeys.allPaged,
        refetchInterval: 60_000,
        staleTime: 60_000,
        enabled
      }
    )

  React.useEffect(() => {
    let timeout
    if (
      pagedAllFetchState.hasNextPage &&
      !pagedAllFetchState.isFetchingNextPage
    ) {
      set((state) => {
        state.hasNextPage = true
      })
      timeout = setTimeout(() => {
        pagedAllFetchState.fetchNextPage()
      }, 200)
    } else if (!pagedAllFetchState.hasNextPage) {
      set((state) => {
        state.hasNextPage = false
      })
    }
    return () => clearTimeout(timeout)
  }, [pagedAllFetchState, set])

  return pagedAllFetchState
}

// Conveniently expose the get service endpoint string for external reference.
export function getUseGetServiceEndpoint(serviceId: string | undefined) {
  return serviceId ? `/services/${serviceId}` : ''
}

export function useGetService(
  id?: string,
  queryOptions?: Partial<QueryObserverOptions<UserDefinedService, AxiosError>>
): UseQueryResult<UserDefinedService, AxiosError> {
  const { placeholderData, set } = useServicesStore((state) => ({
    placeholderData: id ? state.services[id] : ({} as UserDefinedService),
    set: state.set
  }))

  const serviceFetchState = useGremlinQuery<UserDefinedService>(
    getUseGetServiceEndpoint(id),
    {},
    {
      placeholderData,
      enabled: Boolean(id),
      ...queryOptions,
      onSuccess: (data) => {
        set((state) => {
          state.services[data.serviceId] = data
        })
      }
    }
  )

  return serviceFetchState
}
